import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Logo } from "@/components/site/Logo";
import { COMPANY } from "@/lib/company";

const HERO_IMG = "https://images.pexels.com/photos/358549/pexels-photo-358549.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1200";

const COPY = {
  en: {
    brandOverline: "Titan Infra · Private access",
    heroTag: "Private space",
    heroTitle: "Infrastructure operated by Titan Infra.",
    back: "Back to site",
    title: "Private access",
    subtitle: "Enter the credentials issued to you by Titan Infra. Accounts are created manually — there is no public registration.",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    invalid: "Invalid email or password.",
    networkError: "We couldn't reach the server. Please try again in a moment.",
    noAccount: "No credentials yet?",
    noAccountBody: "Access to Titan Infra is by invitation only. Contact us if you would like to know more.",
    contactCta: "Contact us",
    lockNote: "Access is restricted to current holders and invited prospects.",
  },
  fr: {
    brandOverline: "Titan Infra · Accès privé",
    heroTag: "Espace privé",
    heroTitle: "L'infrastructure opérée par Titan Infra.",
    back: "Retour au site",
    title: "Accès privé",
    subtitle: "Saisissez les identifiants qui vous ont été remis par Titan Infra. Les comptes sont créés manuellement — il n'y a pas d'inscription publique.",
    email: "Email",
    password: "Mot de passe",
    submit: "Se connecter",
    invalid: "Email ou mot de passe incorrect.",
    networkError: "Impossible de joindre le serveur pour le moment. Réessayez dans un instant.",
    noAccount: "Pas encore d'identifiants ?",
    noAccountBody: "L'accès à Titan Infra se fait sur invitation. Contactez-nous si vous souhaitez en savoir plus.",
    contactCta: "Nous contacter",
    lockNote: "Accès réservé aux détenteurs actuels et aux prospects invités.",
  },
  es: {
    brandOverline: "Titan Infra · Acceso privado",
    heroTag: "Espacio privado",
    heroTitle: "La infraestructura operada por Titan Infra.",
    back: "Volver al sitio",
    title: "Acceso privado",
    subtitle: "Introduce las credenciales que te ha entregado Titan Infra. Las cuentas se crean manualmente — no hay registro público.",
    email: "Email",
    password: "Contraseña",
    submit: "Entrar",
    invalid: "Email o contraseña incorrectos.",
    networkError: "No pudimos conectar con el servidor. Vuelve a intentarlo en un momento.",
    noAccount: "¿Aún no tienes credenciales?",
    noAccountBody: "El acceso a Titan Infra es por invitación. Contáctanos si quieres saber más.",
    contactCta: "Contactar",
    lockNote: "Acceso reservado a los detentores actuales y prospectos invitados.",
  },
};

const AuthShell = ({ children, c }) => (
  <ThemeProvider forceScope="dark">
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background text-foreground">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" data-testid="auth-brand-link" className="flex items-center">
            <Logo theme="light" size={30} />
          </Link>
          <div>
            <p className="text-[11px] tracking-widest uppercase text-white/50">{c.heroTag}</p>
            <p className="mt-4 font-display font-light text-4xl leading-tight tracking-tighter max-w-md">{c.heroTitle}</p>
          </div>
          <p className="text-[11px] tracking-widest uppercase text-white/40">
            {COMPANY.legalName} · {COMPANY.address.city} · {COMPANY.jurisdiction}
          </p>
        </div>
      </div>

      <div className="flex flex-col p-6 md:p-12 justify-between">
        <Link to="/" data-testid="auth-back-home" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> {c.back}
        </Link>
        <div className="max-w-md w-full mx-auto lg:mx-0">{children}</div>
        <p className="text-[11px] tracking-widest uppercase text-muted-foreground">© {new Date().getFullYear()} Strategy Intelligence Solutions LTD</p>
      </div>
    </div>
  </ThemeProvider>
);

export function Login() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      const to = loc.state?.from || (u.role === "admin" ? "/admin" : "/portal");
      nav(to, { replace: true });
    } catch (err) {
      // 400/401 → invalid credentials · everything else (502, network, timeout) → network error
      const status = err?.response?.status;
      if (status === 400 || status === 401 || status === 403) {
        toast.error(c.invalid);
      } else {
        toast.error(c.networkError);
      }
    } finally { setBusy(false); }
  };

  return (
    <AuthShell c={c}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <p className="overline text-muted-foreground">{c.brandOverline}</p>
        <h1 className="mt-4 font-display font-light text-4xl md:text-5xl tracking-tighter">{c.title}</h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">{c.subtitle}</p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
          <Field label={c.email}>
            <Input required type="email" data-testid="login-email" value={email} onChange={e => setEmail(e.target.value)} className="rounded-none h-11 bg-transparent" />
          </Field>
          <Field label={c.password}>
            <Input required type="password" data-testid="login-password" value={password} onChange={e => setPassword(e.target.value)} className="rounded-none h-11 bg-transparent" />
          </Field>
          <Button type="submit" disabled={busy} data-testid="login-submit" className="rounded-none w-full h-11 text-[12px] tracking-widest uppercase">
            {busy ? "…" : c.submit}
          </Button>
        </form>

        <div className="mt-10 flex items-start gap-3 text-[12px] leading-relaxed text-muted-foreground">
          <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <p>{c.lockNote}</p>
        </div>

        <div className="mt-8 border-t border-border pt-8">
          <p className="text-[13px] text-muted-foreground">{c.noAccount}</p>
          <p className="mt-2 text-[13px] text-muted-foreground">{c.noAccountBody}</p>
          <Link to="/contact" data-testid="login-goto-contact" className="mt-3 inline-block text-[13px] text-foreground underline-offset-4 hover:underline">{c.contactCta} →</Link>
        </div>
      </motion.div>
    </AuthShell>
  );
}

// Public /register is closed. This component only redirects to /login for continuity with existing routes.
export function Register() {
  return <Navigate to="/login" replace />;
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-muted-foreground block mb-2">{label}</span>
    {children}
  </label>
);
